import { Form, Formik } from "formik";
import {
  CSVIcon,
  PDFIcon,
} from "../../../assets/imgs";
import DataTable from "../../../components/DataTable/DataTable";
import InfoField from "../../../components/InfoField/InfoField";
import InfoFieldBox from "../../../components/InfoFieldBox/InfoFieldBox";
import Modal from "../../../components/Modal/Modal";
import { Collaborator, PayrollCollaborator } from "../../../store/types/collaborator";
import { useStore } from "../../../store/useStore";
import { Colors } from "../../../utils/constants/style";
import { formatCPF, formatDateTime } from "../../../utils/functions/formatters";
import Checkbox from "../../../components/FormComponents/Checkbox/Checkbox";
import { 
  PayrollGroupStatusColor, 
  REPORT_ORIENTATIONS, 
  REPORT_TYPES 
} from "../../../utils/constants/constants";
import { 
  CloseCircle, 
  MinusCirlce, 
  Refresh, 
  SearchNormal1, 
  TickCircle, 
  TickSquare} from "iconsax-react";
import SimpleSelect from "../../../components/SimpleSelect/SimpleSelect";
import { PayRollGroupProcessStatus, PayRollGroupSatus } from "../../../store/types/timeManager";
import StatusText from "../../../components/StatusText/StatusText";
import RoundedFilledButton from "../../../components/RoundedFilledButton/RoundedFilledButton";
import { useEffect } from "react";
import { searchInItem } from "../../../utils/functions/functions";
import SearchInput from "../../../components/SearchInput/SeachInput";
import useModal from "../../../hooks/useModal";
import PayrollPreviewModal from "./PayrollPreviewModal";
import Tippy from "@tippyjs/react";

interface DetailsPayRollModalProps {
  onClose: () => void;
}

const initialValues = {
  payrollGroupId: "" as string,	
  collaborators: {} as Record<string, any>,
  unified: {} as Record<string, any>,
  format: "" as string,
  orientation: REPORT_ORIENTATIONS[0].value as string,
  consolidated: false as boolean,
};

const statusMap = {
  [PayRollGroupProcessStatus.OK]: <TickCircle size="20" color={Colors.green} />,
  [PayRollGroupProcessStatus.ERROR]: <CloseCircle size="20" color={Colors.red} />
}

const DetailsPayRollModal: React.FC<DetailsPayRollModalProps> = ({
  onClose,
}) => {
  const selectedPayRoll = useStore(
    (state) => state.timeManager.selectedPayRoll
  );
  const isLoadingPayRoll = useStore(
    (state) => state.timeManager.isLoadingPayRoll
  );
  const isLoadingCollaboratorsList = useStore(
    (state) => state.collaborator.isLoadingCollaboratorsList
  );
  const downloadPayroll = useStore(
    (state) => state.report.downloadPayroll
  );
  const isDownloadingReport = useStore(
    (state) => state.report.isDownloadingReport
  );
  const isRefreshingPayRoll = useStore(
    (state) => state.timeManager.isRefreshingPayRoll
  );
  const collaboratorsSelected = useStore((state) => state.timeManager.collaborators.collaboratorsSelected);
  const collaborators = useStore((state) => state.collaborator.collaboratorsList.data);

  const isAllChecked = () => {
    return collaborators?.every((collaborators: any) => collaboratorsSelected.includes(collaborators?._id ?? "")) ?? false;
  }
  const selectCollaborator = useStore((state) => state.timeManager.selectCollaborator);
  const unselectCollaborator = useStore((state) => state.timeManager.unselectCollaborator);
  const refreshPayRoll = useStore((state) => state.timeManager.refreshPayRoll);
  const bulkRefreshPayRoll = useStore((state) => state.timeManager.bulkRefreshPayRoll);
  const handleBulkRefreshPayRoll = (collaborators: string[]) => {
    if(!selectedPayRoll?._id) return;
    if(!collaborators.length) return;

    bulkRefreshPayRoll({ payRollGroupId: selectedPayRoll?._id ?? '', collaboratorIds: collaborators });
  }
  const setCollaboratorsLimitPerPage = useStore(
    (state) => state.collaborator.collaboratorsList.setLimitPerPage
  );
  const resetCollaboratorListState = useStore(
    (state) => state.collaborator.resetCollaboratorListState
  );
  const collaboratorsAux = useStore(
    (state) => state.collaborator.collaboratorsList.dataAux
  );
  const setCollaborators = useStore(
    (state) => state.collaborator.collaboratorsList.setData
  );
  const getPayrollPreview = useStore(
    (state) => state.report.getPayrollPreview
  );
  const handleInMemorySearch = (value: string) => {
    const sanitizedValue = value.replace(/[^a-zA-Z0-9]/g, "");
    const filtered = collaboratorsAux.filter((item: any) => {
      return columns.some((column: any) => searchInItem(item[column.key], sanitizedValue));
    });
    setCollaborators(filtered);
  };
  const { pushModal } = useModal();
    
  const handleShowPreview = (item_id: string) => {
    getPayrollPreview(item_id, selectedPayRoll?._id as string)
    pushModal(<PayrollPreviewModal collaboratorId={item_id} onClose={() => {}} />);
  };

  useEffect(() => {
    setCollaboratorsLimitPerPage(999);
    return () => {
      resetCollaboratorListState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    { key: "name", label: "Nome" },
    {
      key: "cpf",
      label: "CPF",
      render: (item: PayrollCollaborator) => formatCPF(item.cpf),
    },
    {
      key: "position",
      label: "Cargo",
      render: (item: Collaborator) => item.position?.name ?? '-',
    },
    {
      key: "section",
      label: "Seção",
      render: (item: Collaborator) => item.section?.name ?? '-',
    },
    {
      key: "last_process",
      label: "Último Processamento",
      render: (item: PayrollCollaborator) => {
        const lastProcess = selectedPayRoll?.users?.find((user) => user._id === item._id)?.last_process;
        return (
          <div className="flex px-6 justify-center gap-2">
            { lastProcess?.date ? formatDateTime(lastProcess?.date) : '-' }
          </div>
        )
      },
    },
    {
      key: "signed",
      label: "Assinatura",
      render: (item: PayrollCollaborator) => {
        const signed_at = selectedPayRoll?.users?.find((user) => user._id === item._id)?.signed_at;
        return (
          <div className="flex px-6 justify-center">
            { signed_at ? 
              <Tippy
                  content={`Assinada em:\n${formatDateTime(signed_at)}`}
                  className="px-2 py-1 bg-gray_3 text-sm rounded-md text-white font-medium"
                  duration={1}
              >
                <TickCircle size="24" color={Colors.green} variant="Bold" />
              </Tippy>
              : 
              <MinusCirlce size="24" color={Colors.orange} /> }
          </div>
        )
      },
    }
  ];

  function addSignedAtToCollaborators(collaborators: any[]) {
    return collaborators.map(collaborator => ({
      ...collaborator,
      signed_at: selectedPayRoll?.users?.find((user) => user._id === collaborator._id)?.signed_at
    }));
  }
    
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, { resetForm }) => {
        const collaborators = Object.keys(values.collaborators).filter(
          (k: string) => values.collaborators[k]
        );
        const unified = Object.keys(values.unified).filter(
          (k: string) => values.unified[k]
        );
        if(!collaborators.length) return;
        if(!selectedPayRoll?._id) return;

        downloadPayroll(collaborators, selectedPayRoll?._id as string, unified ?? [], values.format, values.orientation, values.consolidated);
        for(const item of collaborators) unselectCollaborator(item);
        resetForm();
      }}
    >
    {(formik) => (
      <Modal
        title="Detalhes de folha de pagamento"
        icon={<SearchNormal1 size="30" color={Colors.white} variant="Linear" />}
        className="w-[1100px] min-h-[92%]"
        onClose={() => {
          onClose()
          for(const item of collaborators) {
            unselectCollaborator(item._id);
            formik.setFieldValue(
              `collaborators.${item._id}`,
              false
            )
          }
        }}
      >
        <div className="flex flex-col gap-5">
          <InfoFieldBox>
            <InfoField
              loading={isLoadingPayRoll}
              label="Nome"
              content={selectedPayRoll?.name}
            />
            <InfoField
              loading={isLoadingPayRoll}
              label="Lote"
              content={selectedPayRoll?.batch}
            />
          </InfoFieldBox>
          <InfoFieldBox>
            <InfoField
              loading={isLoadingPayRoll}
              label="Início da competência"
              content={formatDateTime(selectedPayRoll?.competence_start)}
            />
            <InfoField
              loading={isLoadingPayRoll}
              label="Fim da competência"
              content={formatDateTime(selectedPayRoll?.competence_end)}
            />
          </InfoFieldBox>
          <InfoFieldBox>
            <InfoField
            loading={isLoadingPayRoll}
            label="Quantidade de Folhas"
            content={selectedPayRoll?.users?.length}
            />
            <InfoField
            loading={isLoadingPayRoll}
            label="Status"
            content={
              (selectedPayRoll?.status?.type && selectedPayRoll?.status?.name)  ? 
                <div className="flex">
                  <StatusText
                    status={PayrollGroupStatusColor[selectedPayRoll.status.type as PayRollGroupSatus]}
                    text={selectedPayRoll.status.name ?? '-'}
                  />
                </div>
              : '-'
              }
            />
          </InfoFieldBox>
          <Form>
            { 
              selectedPayRoll?.status?.type !== PayRollGroupSatus.CLOSED &&
              selectedPayRoll?.status?.type !== PayRollGroupSatus.CLOSING ?
                (
                  isLoadingPayRoll ? null :
                    <div className="flex gap-5">
                      <div className="w-[320px]">
                        <div className="text-dark-blue font-bold my-4">
                          Orientação da Folha
                        </div>
                        <div>
                          <SimpleSelect
                            options={REPORT_ORIENTATIONS}
                            onChange={(e) => {
                              formik.setFieldValue(
                                'orientation',
                                e.target.value.toString()
                              )
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <div className="text-dark-blue font-bold my-4">
                          Unificar Relatórios em PDF
                        </div>
                        <div className="flex flex-wrap items-start">
                          {
                            REPORT_TYPES.map((report_type: any) => {
                              return (
                                <div className="bg-gray_0 p-1 rounded-[7px] mr-2 mb-2">
                                  <Checkbox
                                    key={report_type.value}
                                    onChange={() => {
                                      formik.setFieldValue(
                                        `unified.${report_type.value}`,
                                        !formik.values['unified'][report_type.value]
                                      )
                                    }}
                                    className="w-[14px] h-[14px]"
                                    labelClassName="font-light font-base"
                                    checked={formik.values?.unified?.[report_type.value] ?? false}
                                    text={report_type.label}
                                  />
                                </div>
                              )
                            })
                          }
                        </div>
                      </div>
                    </div>
                )
              : null
            }
            <div className="flex justify-between my-4">
              <div className="text-dark-blue font-bold">
                Colaboradores
              </div>

              <SearchInput
                placeholder="Filtrar registros"
                className="w-[300px]"
                onChange={handleInMemorySearch} />
            </div>
            <div className="px-5">
              <DataTable
                data={(collaborators.length ? addSignedAtToCollaborators(collaborators) : selectedPayRoll?.users) || []}
                columns={columns}
                onDetails={null}
                onEdit={null}
                onReprocess={(item) => {
                  refreshPayRoll({ payRollGroupId: selectedPayRoll?._id ?? '', collaboratorId: item })}
                }
                onShowPreview={(item) => {
                  handleShowPreview(item);
                }}
                showReprocessByItem={() => (selectedPayRoll?.status?.type !== PayRollGroupSatus.CLOSED && selectedPayRoll?.status?.type !== PayRollGroupSatus.CLOSING)}
                showPreviewByItem={() => (selectedPayRoll?.status?.type !== PayRollGroupSatus.CLOSED && selectedPayRoll?.status?.type !== PayRollGroupSatus.CLOSING)}
                loading={isLoadingCollaboratorsList || isLoadingPayRoll}
                isAllChecked={isAllChecked}
                onSelectAll={() => {
                  if(!isAllChecked()) {
                    for(const item of collaborators) {
                      selectCollaborator(item._id);
                      formik.setFieldValue(
                        `collaborators.${item._id}`,
                        true
                      )
                    }
                  } else {
                    for(const item of collaborators) {
                      unselectCollaborator(item._id);
                      formik.setFieldValue(
                        `collaborators.${item._id}`,
                        false
                      )
                    }
                  }
                }}
                isChecked={(item) => !!collaboratorsSelected.includes(item._id)}
                onSelect={(item) => {
                  if(collaboratorsSelected.includes(item._id)) {
                    unselectCollaborator(item._id)
                  } else {
                    selectCollaborator(item._id)
                  }
                  formik.setFieldValue(
                    `collaborators.${item._id}`,
                    !formik.values['collaborators'][item._id]
                  )
                }}
              />
            </div>
            {
              isLoadingPayRoll ? null :
              <div className="flex gap-5 z-30 py-4 sticky -bottom-6 bg-white">
                <div className="flex flex-1 flex-col">
                {
                    selectedPayRoll?.status?.type !== PayRollGroupSatus.CLOSED &&
                    selectedPayRoll?.status?.type !== PayRollGroupSatus.CLOSING ?
                    <div>
                      <span className="font-bold text-lg text-gray_3 mb-4">Reprocessar</span>
                      <div className="flex-1 my-1 mt-4">
                        <RoundedFilledButton
                          loading={Object.keys(isRefreshingPayRoll).length > 0}
                          disabled={Object.keys(isRefreshingPayRoll).length > 0 || Object.keys(formik.values.collaborators).filter((k: string) => formik.values.collaborators[k]).length === 0}
                          onClick={() => {
                            const signed_payrolls = selectedPayRoll?.users?.filter(
                              (user: any) => user.signed_at
                            );

                            const collaborators = Object.keys(formik.values.collaborators).filter(
                              (k: string) => formik.values.collaborators[k]
                            ).filter(
                              (user_id: string) =>
                                !signed_payrolls?.some((user: any) => user._id === user_id)
                            );

                            handleBulkRefreshPayRoll(collaborators);
                            
                            formik.setFieldValue('collaborators', {});

                            const unselect_collaborators = Object.keys(formik.values.collaborators).filter(
                              (k: string) => formik.values.collaborators[k]
                            )
                            
                            for(const item of unselect_collaborators) {
                              unselectCollaborator(item);
                              formik.setFieldValue(
                                `collaborators.${item}`,
                                false
                              )
                            }
                          }}
                          type='button'
                          className="w-full flex gap-4 justify-center"	
                          icon={
                            <Refresh
                              size="20"
                              color={Colors.white}
                              variant="Linear"
                            />
                          }
                          text={"Reprocessar Folhas"}
                        ></RoundedFilledButton>
                      </div>
                      <div className="flex-1 my-1"></div>
                      <div className="flex-1 my-1"></div>
                    </div>
                  : null}
                </div>
                <div className="flex flex-col flex-1">
                  <div className="flex justify-between">
                    <span className="font-bold text-lg text-gray_3 mb-3">Baixar</span>
                    <span className="font-light text-md text-gray_3 mb-3">Selecionados 
                      {` (${Object.keys(formik.values.collaborators).filter((k: string) => formik.values.collaborators[k]).length})`}  
                    </span>
                  </div>
                  {
                    selectedPayRoll?.status?.type !== PayRollGroupSatus.CLOSED &&
                    selectedPayRoll?.status?.type !== PayRollGroupSatus.CLOSING ?
                    <div className="flex-1 my-1">
                      <RoundedFilledButton
                        loading={isDownloadingReport}
                        disabled={Object.keys(formik.values.collaborators).filter((k: string) => formik.values.collaborators[k]).length === 0}
                        className="w-full flex gap-4 justify-center"
                        onClick={() => {
                          formik.setFieldValue('format', 'csv');
                          formik.setFieldValue('consolidated', true);
                          formik.submitForm();
                        }}
                        type='button'
                        icon={<CSVIcon />}
                        text={'Consolidado (CSV)'}
                      ></RoundedFilledButton>
                    </div>
                    : null
                  }
                  <div className="flex-1 my-1">
                    <RoundedFilledButton
                      loading={isDownloadingReport}
                      disabled={Object.keys(formik.values.collaborators).filter((k: string) => formik.values.collaborators[k]).length === 0}
                      onClick={() => {
                        formik.setFieldValue('format', 'csv');
                        formik.submitForm();
                      }}
                      type='button'
                      className="w-full flex gap-4 justify-center"
                      icon={<CSVIcon />}
                      text={'Individual (CSV)'}
                    ></RoundedFilledButton>
                  </div>
                  <div className="flex-1 my-1">
                    <RoundedFilledButton
                      loading={isDownloadingReport}
                      className="w-full flex gap-4 justify-center"
                      onClick={() => {
                        formik.setFieldValue('format', 'pdf');
                        formik.submitForm();
                      }}
                      type='button'
                      icon={<PDFIcon />}
                      disabled={Object.keys(formik.values.collaborators).filter((k: string) => formik.values.collaborators[k]).length === 0}
                      text={'Individual (PDF)'}
                    ></RoundedFilledButton>
                  </div>
                </div>
              </div>
            }
          </Form>
        </div>
      </Modal>
    )}
  </Formik>
  );
};

export default DetailsPayRollModal;